import { useEffect, useRef, useState } from "react";
import { Compass, LocateFixed, Minus, Plus, RefreshCw } from "lucide-react";
import type { Map, Marker } from "maplibre-gl";
import type {
  Cluster,
  SearchFilters,
  SearchResult,
  StoreMatch,
} from "@little-finds/core";
import workerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
import "maplibre-gl/dist/maplibre-gl.css";
import "./map-experience.css";
import { mapViewport } from "../mapViewport";

type Props = {
  result: SearchResult;
  selected: string | null;
  onSelect: (store: StoreMatch) => void;
  filters: SearchFilters;
  onMove: (value: Partial<SearchFilters>) => void;
  onLocate: () => void;
  onCluster: (cluster: Cluster) => void;
};
type Area = Pick<SearchFilters, "center" | "bounds" | "zoom">;
const reducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;
function areaOf(map: Map): Area {
  const center = map.getCenter(),
    bounds = map.getBounds();
  return mapViewport(
    center.lat,
    center.lng,
    bounds.getWest(),
    bounds.getSouth(),
    bounds.getEast(),
    bounds.getNorth(),
    map.getZoom(),
  );
}
function zoomFor(radius: number, latitude: number, width: number) {
  return Math.max(
    2,
    Math.min(
      15,
      Math.log2(
        (40075016 *
          Math.cos((latitude * Math.PI) / 180) *
          Math.max(width, 320)) /
          512 /
          (radius * 1609.344 * 2.4),
      ),
    ),
  );
}

export function OpenStreetMapCanvas({
  result,
  selected,
  onSelect,
  filters,
  onMove,
  onLocate,
  onCluster,
}: Props) {
  const host = useRef<HTMLDivElement>(null),
    map = useRef<Map | null>(null),
    markers = useRef<Marker[]>([]);
  const latest = useRef({ onMove, onSelect, onCluster });
  latest.current = { onMove, onSelect, onCluster };
  const [state, setState] = useState<"loading" | "ready" | "failed">("loading"),
    [attempt, setAttempt] = useState(0);
  const [pending, setPending] = useState<Area | null>(null);
  const [engine, setEngine] = useState<typeof import("maplibre-gl") | null>(
    null,
  );
  const initial = useRef(filters);
  const previousRadius = useRef(filters.radius_miles);
  useEffect(() => {
    let cancelled = false,
      loaded = false;
    let observer: ResizeObserver | undefined;
    setState("loading");
    const timeout = window.setTimeout(() => {
      if (!loaded && !cancelled) setState("failed");
    }, 15000);
    import("maplibre-gl")
      .then((lib) => {
        if (cancelled || !host.current) return;
        lib.setWorkerUrl(workerUrl);
        const f = initial.current;
        const instance = new lib.Map({
          container: host.current,
          style: "https://tiles.openfreemap.org/styles/positron",
          center: [f.center.longitude, f.center.latitude],
          zoom:
            f.zoom ??
            zoomFor(
              f.radius_miles,
              f.center.latitude,
              host.current.clientWidth,
            ),
          minZoom: 1,
          maxZoom: 18,
          renderWorldCopies: true,
          attributionControl: { compact: true },
        });
        map.current = instance;
        instance
          .getCanvas()
          .setAttribute(
            "aria-label",
            "Store map. Use arrow keys to pan and plus or minus to zoom. Store results are also available in the list.",
          );
        instance.on("load", () => {
          if (!cancelled) {
            loaded = true;
            clearTimeout(timeout);
            setEngine(lib);
            setState("ready");
          }
        });
        instance.on("moveend", (event) => {
          if (event.originalEvent && loaded) setPending(areaOf(instance));
        });
        observer = new ResizeObserver(() => instance.resize());
        observer.observe(host.current);
      })
      .catch(() => {
        if (!cancelled) {
          clearTimeout(timeout);
          setState("failed");
        }
      });
    return () => {
      cancelled = true;
      clearTimeout(timeout);
      observer?.disconnect();
      markers.current.forEach((marker) => marker.remove());
      markers.current = [];
      map.current?.remove();
      map.current = null;
    };
  }, [attempt]);

  useEffect(() => {
    const instance = map.current;
    if (!instance || state !== "ready") return;
    setPending(null);
    if (filters.scope === "us" && !filters.bounds) {
      instance.fitBounds(
        [
          [-188, 18],
          [-66, 72],
        ],
        { padding: 30, duration: reducedMotion() ? 0 : 450 },
      );
      return;
    }
    const radiusChanged = previousRadius.current !== filters.radius_miles;
    previousRadius.current = filters.radius_miles;
    const zoom = radiusChanged
      ? zoomFor(
          filters.radius_miles,
          filters.center.latitude,
          host.current?.clientWidth ?? 700,
        )
      : (filters.zoom ?? instance.getZoom());
    instance.easeTo({
      center: [filters.center.longitude, filters.center.latitude],
      zoom,
      duration: reducedMotion() ? 0 : 450,
    });
  }, [
    filters.center.latitude,
    filters.center.longitude,
    filters.radius_miles,
    filters.zoom,
    filters.scope,
    Boolean(filters.bounds),
    state,
  ]);

  useEffect(() => {
    const instance = map.current;
    if (!instance || !engine || state !== "ready") return;
    markers.current.forEach((marker) => marker.remove());
    markers.current = [];
    const add = (
      longitude: number,
      latitude: number,
      label: string,
      count: number,
      active: boolean,
      click: () => void,
      cluster = false,
    ) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = `finds-map-pin${active ? " selected" : ""}${cluster ? " cluster" : ""}`;
      button.textContent = cluster ? String(count) : "✦";
      button.title = label;
      button.setAttribute("aria-label", label);
      button.setAttribute("aria-pressed", String(active));
      button.addEventListener("click", (event) => {
        event.stopPropagation();
        click();
      });
      markers.current.push(
        new engine.Marker({ element: button, anchor: "bottom" })
          .setLngLat([longitude, latitude])
          .addTo(instance),
      );
    };
    if (result.clusters.length)
      result.clusters.forEach((cluster) =>
        add(
          cluster.longitude,
          cluster.latitude,
          `${cluster.count} matching stores. Zoom into this area`,
          cluster.count,
          false,
          () => latest.current.onCluster(cluster),
          true,
        ),
      );
    else
      result.stores.forEach((store) =>
        add(
          store.longitude,
          store.latitude,
          `${store.name}, ${store.recent_count} recent product matches`,
          store.recent_count,
          store.id === selected,
          () => latest.current.onSelect(store),
        ),
      );
  }, [result, selected, engine, state]);

  function zoom(direction: number) {
    const instance = map.current;
    if (!instance) return;
    instance.once("moveend", () => setPending(areaOf(instance)));
    instance.easeTo({
      zoom: instance.getZoom() + direction,
      duration: reducedMotion() ? 0 : 200,
    });
  }
  return (
    <div className="map-canvas open-map" aria-label="Location map">
      <div ref={host} className="live-map" />
      {state !== "ready" && (
        <div className="map-unavailable">
          <Compass size={30} />
          <strong>
            {state === "loading"
              ? "Opening your map…"
              : "The map couldn’t connect."}
          </strong>
          <span>
            {state === "loading"
              ? "The same results are ready in the list."
              : "Your search still works. Browse the list or try the map again."}
          </span>
          {state === "failed" && (
            <button
              className="button small"
              onClick={() => setAttempt((value) => value + 1)}
            >
              <RefreshCw size={15} />
              Try again
            </button>
          )}
        </div>
      )}
      {state === "ready" && (
        <>
          <div className="map-label">
            <span className="status-dot" />
            EXPLORE YOUR AREA
          </div>
          <div className="map-controls">
            <button aria-label="Use my location" onClick={onLocate}>
              <LocateFixed size={19} />
            </button>
            <button aria-label="Zoom in" onClick={() => zoom(1)}>
              <Plus size={19} />
            </button>
            <button aria-label="Zoom out" onClick={() => zoom(-1)}>
              <Minus size={19} />
            </button>
          </div>
          {pending && (
            <button
              className="button small search-map-area"
              onClick={() => {
                latest.current.onMove({
                  ...pending,
                  scope: "us",
                  offset: 0,
                });
                setPending(null);
              }}
            >
              Search this area
            </button>
          )}
        </>
      )}
    </div>
  );
}
